import {DateHelper, MessageDialog, StringHelper} from "@bryntum/schedulerpro";
import {userPreferences} from "../../react/context/bryntumContext.jsx";
import bgContrastHelper from "./bgUtils.jsx";
import {
  applyStripedBackground,
  combineDateRanges,
  filterOutRangesInDay,
  transformNonWorkingData
} from "./utils.jsx";
import {MIXED_TASK_COLOR, MIXED_TASK_STYLE, STRIPED_TASK_STYLE} from "../constants/TaskTypes.jsx";
import {SWIVU_TASK_DEFAULT_COLOR} from "../configs/globalConfig.jsx";
import moment from "moment";
import { PLUGINS_KEYS } from "../constants/PluginKeys.jsx";
import { TIMELINE_CONG_KEYS } from "../constants/OtherKeys.jsx";

export function scheduleEventRenderer({ eventRecord, renderData }) {
  // When event sorter is applied, rendering might be triggered early, passing removed event to the renderer
  // https://github.com/Swivu/swivu-client/issues/701
  // TODO: investigate this deeper, ideally renderer should not require such assertion
  if (!eventRecord.project) {
    return;
  }

  // Task might be null if we've create new task and event simultaneously by double clicking in schedule. Event
  // gets updated first and it cannot resolve the task by task ID, because task id was not yet changed.
  const { task } = eventRecord;

  const backgroundColor = task?.color ? task?.color : SWIVU_TASK_DEFAULT_COLOR;

  let globalStyle = userPreferences?.getSetting("taskStyle", null, true);
  let globalColor = userPreferences?.getSetting("taskColor", null, true);
  if (globalStyle === MIXED_TASK_STYLE) globalStyle = '';
  if (globalColor === MIXED_TASK_COLOR) globalColor = '';

  // Need to apply contrast color to text
  const style = {
    color: task?.color ? bgContrastHelper(globalColor || task?.color, '#fff', '#000') : '#000',
    fontSize: "1em",
    textDecoration: eventRecord.archived ? "line-through": "none"
  };

  if (["line", "dashed", "minimal", "hollow"].includes(task?.taskStyle)) {
    style.color = (globalColor || backgroundColor);
  }

  const isStripStyle = task?.taskStyle ? task.taskStyle === STRIPED_TASK_STYLE : globalStyle === STRIPED_TASK_STYLE;
  if (isStripStyle) {
    style.background = applyStripedBackground(globalColor || backgroundColor);
    renderData.eventStyle = STRIPED_TASK_STYLE;
    renderData.eventColor = "";
  } else {
    renderData.eventStyle =  task?.taskStyle || globalStyle || "plain";
    // default empty color is used in case of drag-creating a record
    renderData.eventColor = globalColor || task?.color || renderData.eventColor || "";
  }

  renderData.style = style;
  renderData.wrapperCls['b-sch-swivu-task-style'] = true;

  const lineStylePermitsContrast = (lineStyle) => ![ 'line', 'dashed', 'minimal', 'hollow' ].includes(lineStyle);

  const canApplyBgContrast = isStripStyle || lineStylePermitsContrast(task?.taskStyle || globalStyle);

  let textContent = null;
  if (canApplyBgContrast) {
    textContent = StringHelper.xss`<span style="color:${style.color};text-decoration:${style.textDecoration}">${eventRecord.name}</span>`;
  } else {
    textContent = StringHelper.xss`<span class="theme-text-color" style="text-decoration:${style.textDecoration};">${eventRecord.name}</span>`
  }
  return textContent;
}

// Feature implementation
// https://github.com/Swivu/swivu-client/issues/766
// event argument will be public in 5.2.4
export async function replicateEventsHorizontally({context, event, source: scheduler}) {
  const timelineConfigState = scheduler.features[PLUGINS_KEYS.swivuTimelineConfigurator].state;
  const skipNonWorkingDayDuringReplicating = timelineConfigState[TIMELINE_CONG_KEYS.skipNonWorkingDayDuringReplicating];

  // If option/alt key is not pressed, do nothing
  if (!skipNonWorkingDayDuringReplicating || event.altKey) {
    return;
  }

  const {
    eventRecord,
    // start/end date will already be rounded according to time axis
    startDate,
    endDate,
    // These 2 will be public in 5.2.4
    originalStartDate,
    originalEndDate
  } = context;

  const {
    timeAxis,
    eventStore,
    assignmentStore,
  } = this;
  const eventsToAdd = [];
  const assignmentsToAdd = [];
  const resizingStart = originalStartDate - startDate !== 0;

  // Exit early if user is shrinking the event instead of extending
  if (resizingStart) {
    // Shrinking start: new start is after original start
    if (startDate > originalStartDate) {
      return;
    }
  } else {
    // Shrinking end: new end is before original end
    if (endDate < originalEndDate) {
      return;
    }
  }

  const nonWorkingTimeChecks = timelineConfigState[TIMELINE_CONG_KEYS.nonWorkingTimeChecks];
  const nonWorkingSlots = scheduler.project.nonWorkingTimeStore.query((r) => nonWorkingTimeChecks.includes(r.id));
  const {nonWorkingDays, nonWorkingTimes} = transformNonWorkingData(nonWorkingSlots);

  // Check if there are any non-working days in the drag range
  // If no non-working days are encountered, let normal resize behavior happen
  let hasNonWorkingDaysInRange = false;
  const rangeStartDate = resizingStart ? startDate : originalEndDate;
  const rangeEndDate = resizingStart ? originalStartDate : endDate;

  // Check each tick in the range for non-working days
  const rangeStartTick = timeAxis.getTickFromDate(rangeStartDate);
  const rangeEndTick = timeAxis.getTickFromDate(rangeEndDate);

  for (let i = Math.floor(rangeStartTick); i < Math.ceil(rangeEndTick); i++) {
    const tick = timeAxis.getAt(i);
    if (tick) {
      const day = DateHelper.format(tick.startDate, "dddd");
      if (nonWorkingDays.includes(day) || nonWorkingTimes[day]) {
        hasNonWorkingDaysInRange = true;
        break;
      }
    }
  }

  // If no non-working days in range, exit and allow normal resize
  if (!hasNonWorkingDaysInRange) {
    return;
  }

  let startTick, endTick;

  // Find first and last ticks that we need to fill with events
  if (resizingStart) {
    startTick = timeAxis.getTickFromDate(startDate);
    // If start date is dragged, last created event end date need to connect to original event start.
    // Therefore we ceil tick index to make sure while iterating we will naturally find a tick with
    // intersection.
    endTick = Math.ceil(timeAxis.getTickFromDate(originalStartDate));
  } else {
    // Same for start date, but we floor instead
    startTick = Math.floor(timeAxis.getTickFromDate(originalEndDate));
    endTick = timeAxis.getTickFromDate(endDate);
  }

  // Track consecutive working time ranges to combine them into single events
  let currentRangeStart = null;
  let currentRangeEnd = null;
  let lastEventDuration = null; // Store duration of last created event before non-working day
  let targetEndTick = endTick; // May be extended if we need to match duration

  const finalizeCurrentRange = (isBeforeNonWorkingGap = false) => {
    if (currentRangeStart && currentRangeEnd) {
      const newEvent = eventStore.createRecord({
        taskId: eventRecord.taskId,
        startDate: currentRangeStart,
        endDate: currentRangeEnd
      });
      const newAssignment = assignmentStore.createRecord({
        resourceId: eventRecord.resourceId,
        eventId: newEvent.id
      });
      eventsToAdd.push(newEvent);
      assignmentsToAdd.push(newAssignment);

      // Store duration if this range is being finalized before a non-working gap
      if (isBeforeNonWorkingGap) {
        lastEventDuration = currentRangeEnd.getTime() - currentRangeStart.getTime();
      }

      currentRangeStart = null;
      currentRangeEnd = null;
    }
  };

  // Always use minor ticks
  // We may need to extend beyond endTick if we're applying duration constraints
  let i = startTick;
  while (i < targetEndTick) {
    // Safety check to prevent infinite loop
    if (i >= timeAxis.count) break;

    const tick = timeAxis.getAt(i);
    let {startDate, endDate} = tick;
    const day = DateHelper.format(startDate, "dddd")
    if (skipNonWorkingDayDuringReplicating && nonWorkingDays.includes(day)) {
      // Finalize any accumulated range before skipping non-working day
      finalizeCurrentRange(true);

      // After a gap, check if we need to extend targetEndTick to match duration
      if (lastEventDuration !== null) {
        // Calculate how many ticks we need to match the duration
        // Look ahead to find the tick that would give us the target duration
        let tempTick = i + 1;
        let foundStart = null;

        // Find the next working day start
        while (tempTick < timeAxis.count) {
          const tempTickObj = timeAxis.getAt(tempTick);
          const tempDay = DateHelper.format(tempTickObj.startDate, "dddd");
          if (!nonWorkingDays.includes(tempDay) && !nonWorkingTimes[tempDay]) {
            foundStart = tempTickObj.startDate;
            break;
          }
          tempTick++;
        }

        if (foundStart) {
          // Get the end date from the context (where user dragged to)
          const userEndDate = resizingStart ? originalStartDate : context.endDate;

          // Calculate the natural duration from next working day start to user's end
          const naturalDuration = userEndDate.getTime() - foundStart.getTime();

          if (naturalDuration >= lastEventDuration) {
            // Natural duration is sufficient, don't extend
            targetEndTick = endTick;
          } else {
            // Need to extend to match duration
            const targetEndDate = new Date(foundStart.getTime() + lastEventDuration);
            const extendedTickIndex = timeAxis.getTickFromDate(targetEndDate);
            targetEndTick = Math.ceil(extendedTickIndex);
          }
          lastEventDuration = null; // Reset after calculating
        }
      }

      // Ignore nonWorkingDays
      i++;
      continue;
    } else if (skipNonWorkingDayDuringReplicating && nonWorkingTimes[day]) {
      // Finalize any accumulated range before handling non-working times
      finalizeCurrentRange(true);

      // After a gap, check if we need to extend targetEndTick to match duration
      if (lastEventDuration !== null) {
        // Look ahead to find the next working time
        let tempTick = i + 1;
        let foundStart = null;

        while (tempTick < timeAxis.count) {
          const tempTickObj = timeAxis.getAt(tempTick);
          const tempDay = DateHelper.format(tempTickObj.startDate, "dddd");
          if (!nonWorkingDays.includes(tempDay) && !nonWorkingTimes[tempDay]) {
            foundStart = tempTickObj.startDate;
            break;
          }
          tempTick++;
        }

        if (foundStart) {
          // Get the end date from the context (where user dragged to)
          const userEndDate = resizingStart ? originalStartDate : context.endDate;

          // Calculate the natural duration from next working day start to user's end
          const naturalDuration = userEndDate.getTime() - foundStart.getTime();

          if (naturalDuration >= lastEventDuration) {
            // Natural duration is sufficient, don't extend
            targetEndTick = endTick;
          } else {
            // Need to extend to match duration
            const targetEndDate = new Date(foundStart.getTime() + lastEventDuration);
            const extendedTickIndex = timeAxis.getTickFromDate(targetEndDate);
            targetEndTick = Math.ceil(extendedTickIndex);
          }
          lastEventDuration = null; // Reset after calculating
        }
      }

      // Ignore non working times
      if (resizingStart && i + 1 === endTick) {
        endDate = originalStartDate;
      }
      // Similar for the end
      else if (!resizingStart && i === startTick) {
        startDate = originalEndDate;
      }
      const sortedNonWorkingRangesOfDay = nonWorkingTimes[DateHelper.format(startDate, "dddd")].sort((a, b) => a.startTime.localeCompare(b.startTime));
      filterOutRangesInDay(sortedNonWorkingRangesOfDay, startDate).forEach((dateRange) => {
        let eventStartDate = dateRange.startDate;
        let eventEndDate = dateRange.endDate;

        // Always use minor tick alignment - use the date range start date

        const newEvent = eventStore.createRecord({
          taskId: eventRecord.taskId,
          startDate: eventStartDate,
          endDate: eventEndDate
        });
        const newAssignment = assignmentStore.createRecord({
          resourceId: eventRecord.resourceId,
          eventId: newEvent.id
        });
        eventsToAdd.push(newEvent);
        assignmentsToAdd.push(newAssignment);
      })
      i++;
    } else {
      // Working day - accumulate into current range
      // If we are resizing start and this is the last tick, we take event start for last event end
      if (resizingStart && i + 1 === endTick) {
        endDate = originalStartDate;
      }
      // Similar for the end
      else if (!resizingStart && i === startTick) {
        startDate = originalEndDate;
      }

      // Start a new range or extend the current one
      if (!currentRangeStart) {
        currentRangeStart = startDate;
        currentRangeEnd = endDate;
      } else {
        // Extend the current range
        currentRangeEnd = endDate;
      }

      i++;
    }
  }

  // Finalize any remaining accumulated range after the loop
  finalizeCurrentRange();

  // let the resize feature know we're taking control over resize
  context.async = true;
  // finalize resize discarding changes - we need event not to be resized
  context.finalize(false);

  if (eventsToAdd.length >= 50) {
    const response = await MessageDialog.confirm({
      okButton: "Proceed",
      cancelButton: "Cancel",
      title: `Creating ${eventsToAdd.length} task events`,
      message: `${eventsToAdd.length} are being created at the same time which could take some to resolve`
    })
    if (response.okButton) {
      eventStore.add(eventsToAdd);
      assignmentStore.add(assignmentsToAdd);
    }
  } else {
    eventStore.add(eventsToAdd);
    assignmentStore.add(assignmentsToAdd);
  }
}

// This listener is required to update event start date to pin it to custom date. Normally that is done via
// timeResolution config for the view preset: https://bryntum.com/docs/gantt/api/Scheduler/preset/ViewPreset#field-timeResolution
// But that has some side effects like date in the tooltip. Also we'd need to redefine zoom levels.
export function snapEventToTickOnDrop({eventRecords, domEvent}) {
  if (!domEvent.altKey) {
    eventRecords.forEach(r => {
      // get the tick index from start date, it will return a float number which we can round
      // and thus get either start or end of the tick
      let tick = Math.round(this.timeAxis.getTickFromDate(r.startDate));

      // Make sure tick index is withing the array boundaries
      tick = Math.max(0, Math.min(tick, this.timeAxis.count - 1));

      r.startDate = this.timeAxis.getAt(tick).startDate;
    });
  }
}

export function snapEventToTickOnDrag({startDate, snapTo}) {
  const {client} = this;

  // get the tick index from start date, it will return a float number which we can round
  // and thus get either start or end of the tick
  let tick = Math.round(client.timeAxis.getTickFromDate(startDate));

  // Make sure tick index is withing the array boundaries
  tick = Math.max(0, Math.min(tick, client.timeAxis.count - 1));

  snapTo.x = client.getCoordinateFromDate(client.timeAxis.getAt(tick).startDate);
}

export function getMajorTickFromMinor(scheduler, selectedDate) {
  const momentSelectedDate = moment(selectedDate);
  const [{ cells }] = scheduler.timeAxisColumn.timeAxisView.levels;
  return cells.find((cell) => momentSelectedDate.isBetween(cell.start, cell.end, undefined, "[)"));
}

export function getWorkingRangesInScheduler(selectedDate, scheduler) {
  const timelineConfigState = scheduler.features[PLUGINS_KEYS.swivuTimelineConfigurator].state;
  const canSkipNonWorking = timelineConfigState[TIMELINE_CONG_KEYS.skipNonWorkingDayDuringReplicating];
  // Always use minor tick alignment
  const tickIndex = scheduler.timeAxis.getTickFromDate(selectedDate);
  const selectedTick = scheduler.timeAxis.getAt(Math.floor(tickIndex));
  const tickStartDate = selectedTick.startDate ?? selectedTick.start;
  const tickEndDate = selectedTick.endDate ?? selectedTick.end;
  if (canSkipNonWorking) {
    const workingDateRanges = [];
    const nonWorkingTimeChecks = timelineConfigState[TIMELINE_CONG_KEYS.nonWorkingTimeChecks];
    const nonWorkingSlots = scheduler.project.nonWorkingTimeStore.query((r) => nonWorkingTimeChecks.includes(r.id));
    const { nonWorkingDays, nonWorkingTimes  } = transformNonWorkingData(nonWorkingSlots);
    let startDate = moment(tickStartDate);
    while (startDate.isBefore(tickEndDate)) {
      const currentDay = startDate.format("dddd");
      const nextDate = moment(startDate).add(1, "day");
      if (!nonWorkingDays.includes(currentDay)) {
        if (!nonWorkingTimes[currentDay]) {
          if (nextDate.isBefore(tickEndDate)) workingDateRanges.push({ startDate: startDate.toDate(), endDate: nextDate.toDate() })
          else workingDateRanges.push({ startDate: startDate.toDate(), endDate: tickEndDate })
        } else {
          const sortedNonWorkingRangesOfDay = nonWorkingTimes[currentDay].sort((a, b) => a.startTime.localeCompare(b.startTime));
          workingDateRanges.push(...filterOutRangesInDay(sortedNonWorkingRangesOfDay, startDate.toDate()))
        }
      }
      startDate = nextDate;
    }
    return combineDateRanges(workingDateRanges);
  } else {
    return [{ startDate: tickStartDate, endDate: tickEndDate }]
  }
}

export const isOverlappingOrEqualDateRanges = (dateRange1, dateRange2) => {
  // Determine which date range starts earlier
  const isDateRange1LesserOrEqual = (
    DateHelper.isBefore(dateRange1.startDate, dateRange2.startDate) ||
    DateHelper.isEqual(dateRange1.startDate, dateRange2.startDate)
  )
  /**
   * Check for overlap
   *
   * A date range overlaps if its start date falls *before* the previous range’s end date.
   * This means that the `laterStart` (which belongs to the second range) must be
   * strictly before `earlierEnd` for there to be an actual overlap.
   *
   * ┌───────────────┐   (Earlier Range)
   *     ┌───────────────┐   (Later Range)
   *     Overlapping case: laterStart < earlierEnd → returns true
   */
  return isDateRange1LesserOrEqual
    ? DateHelper.isBefore(dateRange2.startDate, dateRange1.endDate)
    : DateHelper.isBefore(dateRange1.startDate, dateRange2.endDate);
}

export const isDateRangeInsideLimit = (dateRange, limitRange) => {
  return DateHelper.isAfter(dateRange.endDate, limitRange.startDate) && DateHelper.isAfter(limitRange.endDate, dateRange.startDate)
}

export const getIsMajorTick = (targetElement) => {
  const target = targetElement.classList.contains("b-sch-header-time-axis-cell") ? targetElement : targetElement.closest(".b-sch-header-time-axis-cell");
  return target && !target.parentNode?.previousSibling;
}

// Prevent Duplication Of Events
export function removeDuplicateOverlappingEvents(actionEventRecord, resource) {
  let { startDate, endDate, taskId } = actionEventRecord;

  if (!resource || !startDate || !endDate) return;

  const actionStart = startDate.getTime();
  const actionEnd = endDate.getTime();

  // Filter events to only include those that match or fall within the action event's start & end dates
  const currentResourceEvents = resource.events.filter(ev => {
    // only match events which has taskId same as the actionEventRecord taskId, also make sure the same actionEventRecord not being compared
    if (ev.taskId !== taskId || ev.id === actionEventRecord.id) return false; // Ignore events with different taskIds

    const evStart = ev.startDate.getTime();
    const evEnd = ev.endDate.getTime();

    return (
      (evStart >= actionStart && evEnd <= actionEnd) || // Event falls within action event
      (evStart <= actionStart && evEnd >= actionEnd) || // Action event falls within existing event
      (evStart < actionEnd && evEnd > actionStart)      // Partial overlap
    );
  });

  const toRemoveEvents = [];
  let message = "";

  currentResourceEvents.forEach(ev => {
    if (ev.originalData.swivuCopiedFromColumnFeature) {
      ev.originalData.swivuCopiedFromColumnFeature = false;
      return;
    }
    /** Case 1: Exact Match - Keep the action event, remove the other
     * ┌──────────────┐    (Action Event)
     * ┌──────────────┐    (Existing Event - Will be removed)
     *
     * When two events have identical start and end dates,
     * we keep the action event and remove the existing one.
     */
    if (DateHelper.isEqual(ev.startDate, startDate) && DateHelper.isEqual(ev.endDate, endDate)) {
      // We could have remove activeRecord here but imaging a case where an activeRecord is placed on range where there are more than one exist events of same date ranges exists
      toRemoveEvents.push(actionEventRecord);
      message = `Removing duplicate event "${actionEventRecord.name}" as it is identical to an existing event "${ev.name}", scheduled on (${ev.startDate.toLocaleDateString()} - ${ev.endDate.toLocaleDateString()}).`
      return;
    }

    /** Case 2: Action Event Contains Existing Event - Remove the shorter event (existing)
     * ┌───────────────────┐    (Action Event - Longer)
     *   ┌───────────────┐      (Existing Event - Shorter, will be removed)
     *
     * When the action event completely contains the existing event,
     * the existing event is shorter, so we remove it.
     */
    if (DateHelper.timeSpanContains(startDate, endDate, ev.startDate, ev.endDate)) {
      toRemoveEvents.push(ev);
      message = `Removing an existing event "${ev.name}", scheduled on (${ev.startDate.toLocaleDateString()} - ${ev.endDate.toLocaleDateString()}) as it is fully overlapped by event "${actionEventRecord.name}", scheduled on (${actionEventRecord.startDate.toLocaleDateString()} - ${actionEventRecord.endDate.toLocaleDateString()}).`
      return;
    }

    /** Case 3: Existing Event Contains Action Event - Remove the shorter event (action)
     *   ┌───────────────┐       (Action Event - Shorter if duration is less)
     * ┌───────────────────┐     (Existing Event - Longer if duration is more)
     *
     * When the existing event completely contains the action event,
     * we need to determine which one is shorter by comparing durations.
     * We keep the longer one and remove the shorter one.
     */
    if (DateHelper.timeSpanContains(ev.startDate, ev.endDate, startDate, endDate)) {
      // Calculate durations in milliseconds by getTime() and compare
      const actionEventDuration = endDate.getTime() - startDate.getTime();
      const existingEventDuration = ev.endDate.getTime() - ev.startDate.getTime();
      // If action event is shorter or equal, remove it by returning early
      // (do nothing further with other events)
      if (actionEventDuration <= existingEventDuration) {
        toRemoveEvents.push(actionEventRecord);
        message = `Removing event "${actionEventRecord.name}", scheduled on (${actionEventRecord.startDate.toLocaleDateString()} - ${actionEventRecord.endDate.toLocaleDateString()}) as it is fully overlapped by another event "${ev.name}", scheduled on (${ev.startDate.toLocaleDateString()} - ${ev.endDate.toLocaleDateString()}).`
        return;
      }
    }

    /** Case 4: Partial Overlap - Don't remove anything ( Not Handled This Case, Just for Reference )
     * ┌────────────────┐
     *         ┌───────────────────┐
     *
     * OR
     *
     *         ┌────────────────┐
     * ┌───────────────────┐
     *
     * When events only partially overlap, we don't remove anything
     * This case is implicitly handled by not matching any of the above conditions
     */
  });

  if (toRemoveEvents.length) {
    console.warn("[removeDuplicateOverlappingEvents] Removing overlapping event(s)", toRemoveEvents);

    if (actionEventRecord.originalData.swivuIsAddedByTaskHistoryTab) {
      MessageDialog.alert({
        title: "Removing Duplicate Event",
        message
      }).then(() => {
        setTimeout(() => {
          actionEventRecord.originalData.swivuIsAddedByTaskHistoryTab = false;
          toRemoveEvents.forEach((e) => {
            e.remove();
          })
        }, 30);
      });
    } else {
      // adding a delay as in some actions such as copying from another resource and paste cause this UI to lag as the function is keep calling due to the infinite update listener calls
      setTimeout(() => {
        toRemoveEvents.forEach((e) => {
          e.remove();
        })
      }, 0);
    }
  }
}
